// File: frontend/src/components/seo-analytics/AhrefsFullReportTab.jsx
// Version: 1.0
// Date: 2026-04-28
// Description: Full Ahrefs Report tab — three quick-link cards (Site
//              Explorer, Site Audit, Rank Tracker) deep-linking into the
//              Ahrefs UI in a new tab. Optionally embeds a Looker Studio
//              dashboard above the cards when the env var
//              REACT_APP_LOOKER_STUDIO_URL is set at build time. Light
//              editorial styling matches the other Ahrefs tabs.
// History:
// - [v1.0]: Initial implementation (Phase 07-04 Task A).

import React from 'react';
import { Globe, Activity, LineChart } from 'lucide-react';

const stripProtocol = (url) => {
  if (!url) return '';
  return url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .toLowerCase();
};

const cardWrapStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.75rem',
  padding: '1.25rem 1.25rem 1.5rem',
  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const linkBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.4rem',
  padding: '0.5rem 0.875rem',
  borderRadius: '0.5rem',
  border: '1px solid #2563eb',
  background: '#2563eb',
  color: '#ffffff',
  fontSize: '0.85rem',
  fontWeight: 600,
  textDecoration: 'none',
  cursor: 'pointer',
  width: 'fit-content',
  transition: 'background 0.15s, border-color 0.15s',
};

const QuickLinkCard = ({ Icon, title, href, description, footnote }) => (
  <div style={cardWrapStyle}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '2.25rem',
          height: '2.25rem',
          borderRadius: '0.5rem',
          background: '#eff6ff',
          color: '#2563eb',
          flexShrink: 0,
        }}
      >
        <Icon size={20} strokeWidth={2} />
      </span>
      <h4
        style={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#0f172a',
          margin: 0,
          lineHeight: 1.25,
        }}
      >
        {title}
      </h4>
    </div>
    <p
      style={{
        color: '#475569',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        margin: 0,
      }}
    >
      {description}
    </p>
    {footnote && (
      <p
        style={{
          color: '#94a3b8',
          fontSize: '0.78rem',
          fontStyle: 'italic',
          margin: 0,
          lineHeight: 1.45,
        }}
      >
        {footnote}
      </p>
    )}
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={linkBtnStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#1d4ed8';
        e.currentTarget.style.borderColor = '#1d4ed8';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#2563eb';
        e.currentTarget.style.borderColor = '#2563eb';
      }}
    >
      Open in Ahrefs ↗
    </a>
  </div>
);

const AhrefsFullReportTab = ({ domain }) => {
  const cleanDomain = stripProtocol(domain?.url);
  const encoded = encodeURIComponent(cleanDomain);

  // Note: Site Explorer accepts the bare domain in the `target` query param.
  const siteExplorerUrl = cleanDomain
    ? `https://app.ahrefs.com/site-explorer/overview/v2/subdomains?target=${encoded}`
    : 'https://app.ahrefs.com/site-explorer';
  const siteAuditUrl = 'https://app.ahrefs.com/site-audit/projects';
  const rankTrackerUrl = 'https://app.ahrefs.com/rank-tracker/projects';

  // Optional Looker Studio embed — set REACT_APP_LOOKER_STUDIO_URL at build
  // time on the deployment to auto-render the dashboard above the cards.
  const lookerUrl = process.env.REACT_APP_LOOKER_STUDIO_URL;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
      <div>
        <h3
          style={{
            fontSize: '1.05rem',
            fontWeight: 700,
            color: '#0f172a',
            margin: 0,
          }}
        >
          Full Ahrefs Report
        </h3>
        <p
          style={{
            color: '#64748b',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            margin: '0.25rem 0 0',
          }}
        >
          Complete site audit, historical trends, and detailed analytics —
          opens in Ahrefs.
        </p>
      </div>

      {lookerUrl && (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '0.5rem',
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
          }}
        >
          <iframe
            src={lookerUrl}
            title="Ahrefs Looker Studio dashboard"
            width="100%"
            height="800"
            frameBorder="0"
            allowFullScreen
            style={{
              border: 0,
              borderRadius: '0.5rem',
              display: 'block',
            }}
          />
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1.125rem',
        }}
      >
        <QuickLinkCard
          Icon={Globe}
          title="Site Explorer"
          href={siteExplorerUrl}
          description="Overview, organic, backlinks, AI citations — the deepest view of how this domain ranks."
        />
        <QuickLinkCard
          Icon={Activity}
          title="Site Audit"
          href={siteAuditUrl}
          description="Health Score, errors, warnings, technical SEO — crawl-based audit covering 170+ on-page checks."
          footnote="Project must be configured in Ahrefs first."
        />
        <QuickLinkCard
          Icon={LineChart}
          title="Rank Tracker"
          href={rankTrackerUrl}
          description="Position tracking with daily updates across desktop and mobile, country, and device segments."
        />
      </div>

      {!lookerUrl && (
        <p
          style={{
            color: '#94a3b8',
            fontSize: '0.78rem',
            fontStyle: 'italic',
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          Looker Studio embed: when configured in Ahrefs UI (Site Explorer →
          Looker Studio button → generate report), set{' '}
          <code
            style={{
              background: '#f1f5f9',
              padding: '0.05rem 0.35rem',
              borderRadius: '0.25rem',
              color: '#475569',
              fontSize: '0.75rem',
            }}
          >
            REACT_APP_LOOKER_STUDIO_URL
          </code>{' '}
          on the deployment. We&apos;ll auto-embed it here on next deploy.
        </p>
      )}
    </div>
  );
};

export default AhrefsFullReportTab;
